<!--
//================================================================================
// ID:-
// ProjectName: 都営住宅募集オンライン申請
// SystemName: 都営住宅募集オンラインシステム
// JavaScriptName:RadioButton
//
// <<Modification History>>
// Version  | Date       | Updated By            | Content
// ---------+------------+-----------------------+--------------------------------
// 01.00.00 | 2021/08/06 | 富士通株式会社         | 新規作成
// 01.01.00 | 2024/03/29 | 富士通Japan株式会社    | 令和5年度改修対応（3次リリース）
//================================================================================
-->
<template>
	<div class="contents__input--radio" :class="[classDiv != '' ? classDiv : '']">
		<label
			v-for="(item, index) in items"
			:key="item.key"
			:class="classItem"
		>
			<input
				type="radio"
				v-bind="$attrs"
				:name="radioGroupName"
				:value="item.key"
				:checked="modelValue === item.key"
				:ref="'refs_' + index"
				:disabled="disabledFlg"
				:tabindex="tabindex"
				@keydown.prevent.right="moveNext(index)"
				@keydown.prevent.left="movePrev(index)"
				@change="$emit('update:modelValue', $event.target.value)"
			>
			<span
				class="radio-dummy"
				:class="[{'contents--error-item': errorFlg}, {'radio-dummy-disabled': disabledFlg}]"
			>
				{{ item.val }}
			</span>
		</label>
	</div>
</template>

<script>
import { co_post } from '@/common/ajaxUtility.js';
import { co_isEmpty } from '@/common/checkUtility.js';
import { co_getMessage } from '@/common/messageUtility.js';

import { CD_HANDLE_TYPE } from '@/const/const.js';
import { API_RADIO_ITEMS } from '@/const/route.js';
import { STORE_ERROR_SET_INPUT_ERR, STORE_ERROR_IS_ERRFLG, STORE_ERROR_IS_INPUT_ERRFLG } from '@/const/store.js';

export default {
	inheritAttrs: false,
	props: {
		modelValue: {type: String, default: ''},				// 項目値
		name: {type: String, default: ''},						// 項目名
		requireFlg: {type: Boolean, default: false},			// チェックの有無
		classItem: {type: String, default: ''},					// ラジオボタンのスタイル
		listItems: {type: Array, default: function() {
				return []
			}
		},														// リスト作成用のjson
		cdType: {type: String, default: ''},					// ラジオボタン作成用のタイプコード
		cdHanyo1: {type: String, default: ''},					// ラジオボタン作成用の汎用コード１
		cdHanyo2: {type: String, default: ''},					// ラジオボタン作成用の汎用コード２
		cdHanyo3: {type: String, default: ''},					// ラジオボタン作成用の汎用コード３
		cdKbn: {type: String, default: CD_HANDLE_TYPE.ID_CD },	// ラジオボタン作成用のコード分類
		radioGroupName: {type: String, default: ''},			// ラジオボタンに設定するname
		disabledFlg: {type: Boolean, default: false},			// 活性/非活性の有無
		tabindex: {type: String, default: '0'},					// タブインデックス
		groupId: {type: String, default: ''},					// 項目グループID
		classDiv: {type: String, default: ''},					// コントロールエリアのスタイル
	},
	emits: ['update:modelValue'],
	data: function() {
		return {
			errorFlg: false,
			items: {},
			focusIndex: null
		}
	},
	computed: {
		errFlg: function () {
			return this.$store.getters[STORE_ERROR_IS_ERRFLG];
		}
	},
	watch: {
		disabledFlg: function( val ) {
			if( val ) {
				this.clearError();
			}
		},
		errFlg: function(newValue) {
			if(newValue === false) {
				this.clearError();
			}
		},
		cdType: async function() {
			// DBからデータを取得しリストを設定
			this.createList(await this.getData());
		},
		listItems: function () {
			// 親から受け取ったjson文字列でリストを設定
			this.createList(this.listItems);
		}
	},
	created: async function() {
		if ( this.cdType != '' ) {
			// DBからデータを取得しリストを設定
			this.createList(await this.getData());

		} else {
			// 親から受け取ったjson文字列でリストを設定
			this.createList(this.listItems);
		}

		// フォーカスの遅延設定
		let that = this;
		this.$nextTick(function() {
			if (that.focusIndex !== null) {
				that.focus(that.focusIndex);
			}
		});
	},
	beforeUnmount: function() {
		this.items = null;
	},
	methods: {
		moveNext: function ( index ) {
			// →押下時のキーイベント
			index = index + 1;
			this.focus(index);
		},
		movePrev: function ( index ) {
			// ←押下時のキーイベント
			index = index - 1;
			this.focus(index);
		},
		// コードマスタからリストの値を取得
		getData: function() {

			// ajaxにてサーバーと通信
			let params = { cdType: this.cdType, cdHanyo1: this.cdHanyo1, cdHanyo2: this.cdHanyo2, cdHanyo3: this.cdHanyo3 };
			return co_post( API_RADIO_ITEMS, params );
		},
		createList: function( val ) {

			let list = [];

			for ( let i = 0; i < val.length; i++ ) {

				if(this.cdKbn === CD_HANDLE_TYPE.CD_HANYO1) {
					// 汎用コード１をコード値として使用
					list.push( {key: val[i].cdHanyo1, val: val[i].nmCd} );
				} else if (this.cdKbn === CD_HANDLE_TYPE.CD_HANYO2) {
					// 汎用コード２をコード値として使用
					list.push( {key: val[i].cdHanyo2, val: val[i].nmCd} );
				} else if (this.cdKbn === CD_HANDLE_TYPE.CD_HANYO3) {
					// 汎用コード３をコード値として使用
					list.push( {key: val[i].cdHanyo3, val: val[i].nmCd} );
				} else {
					// コード値をコード値として使用
					list.push( {key: val[i].idCd, val: val[i].nmCd} );
				}
			}

			this.items = list;
		},
		// 妥当性チェック
		validate: function() {

			let error = false;
			let inputError = this.$store.getters[STORE_ERROR_IS_INPUT_ERRFLG];

			// 必須チェック
			if( this.requireFlg === true ) {
				if( co_isEmpty(this.modelValue) ) {
					this.$store.commit( STORE_ERROR_SET_INPUT_ERR,
								{id: this.groupId, msg: co_getMessage('E0054', this.name)} );
					error = true;
				}
			}

			if (!inputError && error) {
				// エラーの先頭項目の場合、フォーカス設定
				this.focus();
			}

			this.errorFlg = error;
			return !this.errorFlg;
		},
		// エラー設定
		setError: function() {
			this.errorFlg = true;
		},
		// エラークリア
		clearError: function() {
			this.errorFlg = false;
		},
		// フォーカス設定
		focus: function( index ) {
			if ( index === undefined ) {
				index = 0;
			}
			if (this.$refs['refs_' + index] !== undefined) {
				this.$refs['refs_' + index][0].focus();
				this.focusIndex = null;
			} else {
				this.focusIndex = index;
			}
		}
	}
}
</script>

<style scoped>
</style>